// =============================================================================
// Public Booking - Tipi condivisi frontend
// =============================================================================
// SHAPE = identica al backend in
// backend/src/common/constants/public-booking.constants.ts
// Da mantenere in sync manualmente finché non introduciamo un pacchetto shared.
// =============================================================================

export type PublicBookingMode = 'auto' | 'approval';

export type CaptchaProvider = 'hcaptcha' | 'turnstile' | 'recaptcha-v3';

export interface CaptchaConfig {
  enabled: boolean;
  provider: CaptchaProvider | null;
  siteKey: string | null;
  /// secretKey NON è esposto al frontend (omesso nelle response API admin)
  secretKey?: never;
}

export type PublicBookingTheme = 'minimal' | 'hero';

export interface PublicBookingBranding {
  logoUrl: string | null;
  primaryColor: string | null;
  displayName: string | null;
  theme: PublicBookingTheme;
  coverImageUrl: string | null;
  headline: string | null;
  subheadline: string | null;
}

export interface PublicBookingSettings {
  enabled: boolean;
  mode: PublicBookingMode;
  minAdvanceHours: number;
  maxAdvanceDays: number;
  allowGuestCustomers: boolean;
  branding: PublicBookingBranding;
  termsUrl: string | null;
  privacyUrl: string | null;
  captcha: CaptchaConfig;
}

export interface ServicePublicBookingRules {
  publicBookingEnabled: boolean;
  publicBookingMode: PublicBookingMode | null;
  allowPublicResourceSelection: boolean;
  publicVisibleName: string | null;
  publicDescription: string | null;
}

export const DEFAULT_PUBLIC_BOOKING_SETTINGS: PublicBookingSettings = {
  enabled: false,
  mode: 'approval',
  minAdvanceHours: 24,
  maxAdvanceDays: 60,
  allowGuestCustomers: true,
  branding: {
    logoUrl: null,
    primaryColor: null,
    displayName: null,
    theme: 'minimal',
    coverImageUrl: null,
    headline: null,
    subheadline: null,
  },
  termsUrl: null,
  privacyUrl: null,
  captcha: { enabled: false, provider: null, siteKey: null },
};

export const DEFAULT_SERVICE_PUBLIC_BOOKING_RULES: ServicePublicBookingRules = {
  publicBookingEnabled: false,
  publicBookingMode: null,
  allowPublicResourceSelection: false,
  publicVisibleName: null,
  publicDescription: null,
};

// -----------------------------------------------------------------------------
// Booking Request (admin dashboard)
// -----------------------------------------------------------------------------

export type BookingRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'CANCELLED_BY_CUSTOMER';

export type MatchConfidence =
  | 'exact_email'
  | 'exact_phone'
  | 'exact_fiscal_code'
  | 'fuzzy_identity'
  | null;

export interface CustomerSnapshot {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  fiscalCode?: string;
  dateOfBirth?: string;
  notes?: string;
  customFields?: Record<string, unknown>;
}

export interface ProposedAllocation {
  resourceTypeId: string;
  resourceId: string;
  startTime: string;
  endTime: string;
  role: 'primary' | 'support' | 'facility';
  customerPreferred?: boolean;
}

export interface BookingRequest {
  id: string;
  organizationId: string;
  serviceDefinitionId: string;
  requestedStart: string;
  requestedEnd: string;
  proposedAllocations: ProposedAllocation[];
  customerSnapshot: CustomerSnapshot;
  matchedCustomerId: string | null;
  matchConfidence: MatchConfidence;
  status: BookingRequestStatus;
  reviewedById: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  rejectionReason: string | null;
  createdAppointmentId: string | null;
  /** Evento di gruppo esistente a cui la richiesta chiede di unirsi (null = richiesta standard) */
  targetAppointmentId: string | null;
  /** true se la richiesta è un join: all'approve non crea un nuovo appuntamento */
  isJoinRequest: boolean;
  customerLocale: string | null;
  source: 'web' | 'widget';
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}
